import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PrivateAppLayout from '../layouts/PrivateAppLayout';
import AuthRoute from './AuthRoute';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { getCurrentYear } from '../utils/utils';
import { storeAppraisalCycleSelected } from '../modules/Profile/components/AppraisalCycleSlice';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { RootState } from '../store/store';
import TokenCheck from '../utils/TokenCheck';
import LazyLoader from '../components/LazyLoader';

// Lazy load all components for better code splitting
const Dashboard = lazy(() => import('../modules/Dashboard/Dashboard'));
const LoginPage = lazy(() => import('../modules/Login/Login'));
const BUArtifact = lazy(() => import('../modules/BUArtifact').then((module) => ({ default: module.BUArtifact })));
const CompanyArtifact = lazy(() => import('../modules/BUArtifact/CompanyArtifacts'));
const Profile = lazy(() => import('../modules/Profile').then((module) => ({ default: module.Profile })));
const NotFound = lazy(() => import('../modules/NotFound').then((module) => ({ default: module.NotFound })));
const AddArtifacts = lazy(() => import('../modules/Artifact/components/AddArtifacts'));
const ViewAllArtifact = lazy(() => import('../modules/Artifact/components/ViewAllArtifact'));
const Growth = lazy(() => import('../modules/Growth/Growth'));
const ViewArtifactDetails = lazy(() => import('../modules/Artifact/components/ViewArtifact'));
const AddProjectLevelArtifact = lazy(() => import('../modules/Artifact/components').then((module) => ({ default: module.AddProjectLevelArtifact })));
const ArtifactList = lazy(() => import('../modules/Artifact/components/ArtifactList'));
const ViewClone = lazy(() => import('../modules/ViewClone/ViewClone'));
const LiveRankingExpend = lazy(() => import('../modules/Dashboard/components/LiveRanking/LiveRankingExpend'));
const AddEditDesignationForm = lazy(() => import('../modules/Evaluation/AddEditDesignationForm'));
const Analytics = lazy(() => import('../modules/Analytics/Analytics'));
const AnalyticsDetails = lazy(() => import('../modules/Analytics/AnalyticsDetails'));

const AppRouter = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: RootState) => state.roles.isLoading);
  const currentSelectedYear = useSelector((state: any) => state.appraisalCycle.selectedYear);
  const path = window.location.pathname;
  if (!currentSelectedYear) {
    dispatch(storeAppraisalCycleSelected(getCurrentYear()));
  }
  if (isLoading && path !== '/login' && path !== '/*') {
    return <LazyLoader />;
  }
  const authAppRouter = createBrowserRouter([
    {
      path: '/',
      element: <AuthRoute />,
      children: [
        {
          path: '/',
          element: (
            <>
              <TokenCheck />
              <PrivateAppLayout />
            </>
          ),
          children: [
            {
              path: '/',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <Dashboard />
                </Suspense>
              ),
            },
            {
              path: '/team-artifacts',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSTEAMPER" />
                  <BUArtifact />
                </Suspense>
              ),
            },
            {
              path: '/company-artifacts',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="ADGROWTHREP" />
                  <CompanyArtifact />
                </Suspense>
              ),
            },
            {
              path: '/artifacts/add-artifacts',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPER:SUBMYPERDET" actionTypes={['Write']} />
                  <AddArtifacts />
                </Suspense>
              ),
            },
            {
              path: '/artifacts/add-artifacts/form/:text/:id/:categoryCode',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPER:SUBMYPERDET" actionTypes={['Write']} />
                  <AddProjectLevelArtifact />
                </Suspense>
              ),
            },
            {
              path: '/artifact-detail',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPER" />
                  <ArtifactList />
                </Suspense>
              ),
            },
            {
              path: '/user-artifact-detail/:userId',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPER" />
                  <ArtifactList />
                </Suspense>
              ),
            },
            {
              path: '/liveRankingExpand',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSLIVERANK" />
                  <LiveRankingExpend rankings={[]} />
                </Suspense>
              ),
            },
            {
              path: '/Profile',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPROFILE" />
                  <Profile />
                </Suspense>
              ),
            },
            {
              path: '/artifacts/editArtifact/:id/:categoryId/:categoryNames',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPER:SUBMYPERDET" actionTypes={['Edit']} />
                  <AddProjectLevelArtifact />
                </Suspense>
              ),
            },
            {
              path: '/artifacts/viewArtifact/:artifactFormId',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPER:SUBMYPERDET" actionTypes={['Read']} />
                  <ViewArtifactDetails />
                </Suspense>
              ),
            },
            {
              path: '/artifacts/viewAll-artifact/:userId?',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSMYPER:SUBMYPERDET" actionTypes={['Read']} />
                  <ViewAllArtifact />
                </Suspense>
              ),
            },
            {
              path: '/growth',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSGROWTH" />
                  <Growth />
                </Suspense>
              ),
            },
            {
              path: '/viewClone',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSCLONE" />
                  <ViewClone />
                </Suspense>
              ),
            },
            {
              path: '/evaluation/designation/edit',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="ADDSGN" actionTypes={['Edit']} />
                  <AddEditDesignationForm />
                </Suspense>
              ),
            },
            {
              path: '/analytics',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSANALYTICS" />
                  <Analytics />
                </Suspense>
              ),
            },
            {
              path: '/analytics/:detailType',
              element: (
                <Suspense fallback={<LazyLoader />}>
                  <ProtectedRoute code="PMSANALYTICS" />
                  <AnalyticsDetails />
                </Suspense>
              ),
            },
          ],
        },
      ],
      errorElement: <ErrorBoundary />,
    },
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/*',
      element: <NotFound />,
    },
  ]);

  return <RouterProvider router={authAppRouter} />;
};

export default AppRouter;
